import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Droplets, Plus, Eye, Pencil, Trash2, Activity, FlaskConical } from 'lucide-react';
import {
  MapContainer, TileLayer, CircleMarker, Polygon, Polyline, Popup, Tooltip, useMap, useMapEvents,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import orchardService from '../../services/orchard';
import irrigationNetworkService, {
  type IrrigationLiveNetwork,
  type IrrigationNode,
  type IrrigationPipe,
  type NodeType,
  type PipeMaterial,
  type SolveResult,
  type LiveAnalysis,
} from '../../services/irrigationNetwork';
import type { Farm } from '../../types/orchard';
import { errorMessage } from '../../utils/apiResponse';
import NodeEditDrawer from '../../components/irrigation/network/NodeEditDrawer';
import MapTouchGate from '../../components/map/MapTouchGate';
import useRelayCatalogue from '../../hooks/useRelayCatalogue';
import { slotKey, unlinkedProgramSlots, type RelaySlot } from '../../utils/relayCatalogue';
import ZoneDrawControl from '../../components/map/ZoneDrawControl';
import { geoJsonRingToLatLng } from '../../utils/geoLeaflet';
import espnowService from '../../services/espnowIrrigation';
import PipeEditDrawer from '../../components/irrigation/network/PipeEditDrawer';
import ScenariosPanel from '../../components/irrigation/network/ScenariosPanel';
import LiveAnalysisPanel from '../../components/irrigation/network/LiveAnalysisPanel';
import '../../styles/GlobalPageStyles.css';
import { toNum } from '@/utils/numeric';

type Mode = 'view' | 'edit';
type EditTool = 'select' | 'add-node' | 'draw-pipe';

const NODE_COLOURS: Record<NodeType, string> = {
  source:   '#dc2626', // red
  junction: '#6b7280', // grey
  valve:    '#2563eb', // blue
  sensor:   '#eab308', // yellow
  outlet:   '#16a34a', // green
  tank:     '#0891b2', // cyan
};

const NODE_RADIUS: Record<NodeType, number> = {
  source: 10, junction: 5, valve: 8, sensor: 7, outlet: 6, tank: 10,
};

function MapClickHandler({ onClick }: { onClick: (lat: number, lng: number) => void }) {
  useMapEvents({ click(e) { onClick(e.latlng.lat, e.latlng.lng); } });
  return null;
}

/**
 * Fit to the farm boundary, plus whatever network is already drawn. Both the
 * farm and the network load after the map has mounted, so MapContainer's
 * `center` prop is stale by the time it matters — the view has to be set
 * imperatively or the map sits on its fallback coordinates forever.
 */
function FitToFarm({ points, fitKey }: { points: [number, number][]; fitKey: string }) {
  const map = useMap();
  const lastFit = useRef<string | null>(null);

  useEffect(() => {
    if (points.length === 0 || lastFit.current === fitKey) return;
    // No animation: a zoom still in flight when the page unmounts throws from Leaflet.
    map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 19, animate: false });
    lastFit.current = fitKey;
    // `points` is rebuilt every render; fitKey is what actually gates the fit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, fitKey]);

  return null;
}

function velocityColour(v: number): string {
  const a = Math.abs(v);
  if (a > 2.0) return '#dc2626'; // red
  if (a > 1.5) return '#f59e0b'; // amber
  if (a > 0.05) return '#16a34a'; // green
  return '#9ca3af'; // grey (no flow)
}

function divergenceColour(level: 'ok' | 'warn' | 'alert' | 'stale' | 'unknown'): string {
  switch (level) {
    case 'alert': return '#dc2626';
    case 'warn':  return '#f59e0b';
    case 'stale': return '#9ca3af';
    case 'ok':    return '#16a34a';
    default:      return '#6b7280';
  }
}

export default function NetworkMap() {
  const { hasAccess, loading: licLoading } = useModuleLicense('irrigation');

  const [searchParams, setSearchParams] = useSearchParams();
  const farmIdParam = searchParams.get('farm');
  const farmId = farmIdParam ? Number(farmIdParam) : null;

  const [farms, setFarms] = useState<Farm[]>([]);
  const [farm, setFarm] = useState<Farm | null>(null);
  const [network, setNetwork] = useState<IrrigationLiveNetwork | null>(null);
  const [materials, setMaterials] = useState<PipeMaterial[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [mode, setMode] = useState<Mode>('view');
  const [tool, setTool] = useState<EditTool>('select');
  const [pendingNodeType, setPendingNodeType] = useState<NodeType>('valve');
  const [pipeStartNodeId, setPipeStartNodeId] = useState<number | null>(null);
  const [pipeVertices, setPipeVertices] = useState<[number, number][]>([]); // [lng, lat]

  const [selectedNode, setSelectedNode] = useState<IrrigationNode | null>(null);
  const [selectedPipe, setSelectedPipe] = useState<IrrigationPipe | null>(null);

  // Phase 2: scenarios + solve overlay
  const [activeScenarioId, setActiveScenarioId] = useState<number | null>(null);
  const [draftOpenValves, setDraftOpenValves] = useState<Set<number>>(new Set());
  const [solveResult, setSolveResult] = useState<SolveResult | null>(null);
  const [showScenarios, setShowScenarios] = useState(false);
  const [liveAnalysis, setLiveAnalysis] = useState<LiveAnalysis | null>(null);
  const [showLive, setShowLive] = useState(false);


  // Every relay the farm's controllers can address, annotated with what claims
  // it. Loaded once per farm here rather than inside the node drawer, so
  // opening a second node does not re-fetch the site's devices — and so the
  // count of unlinked scheduled relays can be stated before anything is
  // selected, which is where an operator would look for it.
  const {
    catalogue: relayCatalogue,
    routersByController,
    loading: catalogueLoading,
    error: catalogueError,
    applyZone,
  } = useRelayCatalogue(farmId, network?.nodes);

  // The relay whose area is being drawn, if any. Held here rather than in the
  // drawer because the drawing happens on the map — and because it has to
  // survive the network's 30s poll re-rendering the sidebar underneath it.
  const [zoneDraft, setZoneDraft] = useState<RelaySlot | null>(null);
  const unlinkedRelays = useMemo(
    () => unlinkedProgramSlots(relayCatalogue),
    [relayCatalogue],
  );

  // ── Load farms on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await orchardService.getFarms(1, 200);
        if (res.success) {
          const list = (res.data?.farms ?? res.data ?? []) as Farm[];
          setFarms(Array.isArray(list) ? list : []);
        }
      } catch (e) { setError(errorMessage(e, 'Failed to load farms')); }
    })();
  }, []);

  // ── Load materials once
  useEffect(() => {
    irrigationNetworkService.listMaterials()
      .then(setMaterials)
      .catch((e) => setError(errorMessage(e, 'Failed to load pipe materials')));
  }, []);

  // ── Load farm + network when farmId changes
  const loadNetwork = useCallback(async (silent = false) => {
    if (!farmId) return;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [farmRes, net] = await Promise.all([
        orchardService.getFarm(farmId),
        irrigationNetworkService.getLiveNetwork(farmId),
      ]);
      if (farmRes.success) setFarm(farmRes.data);
      setNetwork(net);
    } catch (e) {
      setError(errorMessage(e, 'Failed to load network'));
    } finally {
      setLoading(false);
    }
  }, [farmId]);

  useEffect(() => {
    loadNetwork();
    if (!farmId) return;
    const interval = setInterval(() => loadNetwork(true), 30_000);
    return () => clearInterval(interval);
  }, [farmId, loadNetwork]);

  // Effective solve overlay: explicit scenario solve takes priority; otherwise
  // fall back to live analysis when active.
  const effectiveSolve = solveResult ?? liveAnalysis?.solve ?? null;
  const divergenceByNode = useMemo(() => {
    const m = new Map<number, { level: 'ok'|'warn'|'alert'|'stale'|'unknown'; delta_kpa: number|null; delta_pct: number|null; predicted_kpa: number|null; measured_kpa: number }>();
    for (const d of liveAnalysis?.divergences ?? []) {
      m.set(d.node_id, {
        level: d.level,
        delta_kpa: d.delta_kpa,
        delta_pct: d.delta_pct,
        predicted_kpa: d.predicted_kpa,
        measured_kpa: d.measured_kpa,
      });
    }
    return m;
  }, [liveAnalysis]);

  // ── Map centre/zoom
  const center: [number, number] = useMemo(() => {
    if (network && network.nodes.length > 0) {
      const lats = network.nodes.map(n => n.lat);
      const lngs = network.nodes.map(n => n.lng);
      return [(Math.min(...lats) + Math.max(...lats)) / 2,
              (Math.min(...lngs) + Math.max(...lngs)) / 2];
    }
    const cp = farm?.center_point as unknown;
    if (cp && typeof cp === 'object') {
      // Accept either GeoJSON Point ({coordinates:[lng,lat]}) or {lat,lng} shape
      const coords = (cp as { coordinates?: unknown }).coordinates;
      if (Array.isArray(coords) && coords.length >= 2 &&
          typeof coords[0] === 'number' && typeof coords[1] === 'number') {
        return [coords[1] as number, coords[0] as number];
      }
      const obj = cp as { lat?: unknown; lng?: unknown };
      if (typeof obj.lat === 'number' && typeof obj.lng === 'number') {
        return [obj.lat, obj.lng];
      }
    }
    return [-37.8, 145.0];
  }, [network, farm]);

  // ── Farm boundary as Leaflet [lat, lng] — drawn on the map and fitted to.
  const farmRing = useMemo(() => {
    const ring = farm?.boundary_geometry?.coordinates?.[0];
    if (!ring || ring.length < 3) return null;
    return ring.map(([lng, lat]) => [lat, lng] as [number, number]);
  }, [farm]);

  // ── What the map should have in view: the farm boundary, widened by any
  //    network already placed so nothing drawn outside the boundary is lost.
  const fitPoints = useMemo(() => {
    const pts: [number, number][] = [...(farmRing ?? [])];
    for (const n of network?.nodes ?? []) pts.push([n.lat, n.lng]);
    for (const pipe of network?.pipes ?? []) {
      for (const [lng, lat] of pipe.geometry.coordinates) pts.push([lat, lng]);
    }
    return pts;
  }, [farmRing, network]);

  // Re-fit when the farm changes or its data first arrives — not on every
  // refresh, or the map would yank itself back while someone places nodes.
  const fitKey = `${farmId ?? 'none'}:${fitPoints.length > 0 ? 'loaded' : 'empty'}`;

  // ── Map click handler
  const handleMapClick = useCallback(async (lat: number, lng: number) => {
    if (mode !== 'edit') return;
    // While an area is being drawn every click belongs to the polygon. Without
    // this, placing its third corner also drops a node there.
    if (zoneDraft) return;
    if (tool === 'add-node' && farmId) {
      try {
        // Try elevation lookup (non-blocking; ignore errors)
        let elevation_m: number | null = null;
        let elevation_source: 'dem' | 'manual' | null = null;
        try {
          const dem = await irrigationNetworkService.lookupElevation(lat, lng);
          if (dem) { elevation_m = toNum(dem.elevation_m); elevation_source = 'dem'; }
        } catch { /* ignore */ }

        const node = await irrigationNetworkService.createNode(farmId, {
          node_type: pendingNodeType,
          lat, lng,
          elevation_m, elevation_source,
          control_mode: pendingNodeType === 'valve' ? 'manual' : null,
        });
        await loadNetwork(true);
        setSelectedNode(node);
        setTool('select');
      } catch (e) { setError(errorMessage(e, 'Failed to add node')); }
    } else if (tool === 'draw-pipe' && pipeStartNodeId !== null) {
      // shift-click adds intermediate vertex (handled at marker level for endpoints)
      setPipeVertices((v) => [...v, [lng, lat]]);
    }
  }, [mode, tool, pendingNodeType, farmId, pipeStartNodeId, loadNetwork, zoneDraft]);

  // ── Click on existing node
  const handleNodeClick = useCallback(async (node: IrrigationNode) => {
    if (mode === 'view') {
      setSelectedNode(node);
      return;
    }
    if (tool === 'draw-pipe') {
      if (pipeStartNodeId === null) {
        setPipeStartNodeId(node.id);
        setPipeVertices([[node.lng, node.lat]]);
      } else if (pipeStartNodeId !== node.id && farmId) {
        const start = network?.nodes.find(n => n.id === pipeStartNodeId);
        if (!start) return;
        const coords: [number, number][] = [
          [start.lng, start.lat],
          ...pipeVertices.slice(1),
          [node.lng, node.lat],
        ];
        const defaultMaterial = materials[0]?.key ?? 'pe100';
        try {
          await irrigationNetworkService.createPipe(farmId, {
            from_node_id: pipeStartNodeId,
            to_node_id: node.id,
            geometry: { type: 'LineString', coordinates: coords },
            material_key: defaultMaterial,
            nominal_diameter_mm: 50,
            internal_diameter_mm: 44,
            pressure_class_kpa: null,
            roughness_override: null,
            installed_year: null,
            notes: null,
          });
          setPipeStartNodeId(null);
          setPipeVertices([]);
          setTool('select');
          await loadNetwork(true);
        } catch (e) { setError(errorMessage(e, 'Failed to create pipe')); }
      }
    } else {
      setSelectedNode(node);
    }
  }, [mode, tool, pipeStartNodeId, pipeVertices, network, farmId, materials, loadNetwork]);

  const handleFarmChange = (id: number) => {
    setSelectedNode(null);
    setSelectedPipe(null);
    setSearchParams({ farm: String(id) });
  };

  // ── Renders
  if (licLoading) return <div className="max-w-7xl mx-auto px-4 py-6">Loading…</div>;
  if (!hasAccess) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Card><CardContent className="p-4">An Irrigation module subscription is required to view the network map.</CardContent></Card>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Irrigation Network"
        subtitle={farm?.name ?? 'Pick a farm'}
        icon={<Droplets className="w-6 h-6 text-blue-600" />}
      />
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Farm selector + mode toggle */}
        {/* `CardContent` is written to follow a `CardHeader` — no top padding
            and a deep bottom — so a bar with no header states its own padding,
            or the controls sit against the top edge over an empty strip. */}
        <Card className="mb-3">
          <CardContent className="p-3 sm:px-4 flex flex-wrap items-center gap-x-3 gap-y-2">
            {/* Label and select travel as one item: wrapped separately, a
                phone put "Farm:" on a line of its own above the select. */}
            <label className="flex items-center gap-2 w-full min-w-0 sm:w-auto">
              <span className="text-sm font-medium">Farm</span>
              <select
                value={farmId ?? ''}
                onChange={(e) => handleFarmChange(Number(e.target.value))}
                className="border rounded px-2 py-1.5 text-sm min-w-0 flex-1 sm:flex-none sm:max-w-xs"
              >
                <option value="">— select —</option>
                {farms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </label>

            {/* Below `sm:` every label here is hidden and only the icon is
                left, so each button carries an `aria-label` — without one a
                phone shows four unnamed controls, and a screen reader four
                empty buttons. Scenarios and Live also need distinct icons for
                the same reason: they shared `Activity` and were therefore
                indistinguishable on the width where the words are gone.

                Two groups, because they are two kinds of control. Scenarios and
                Live each open a panel and can both be on; View and Edit are one
                either/or switch. Styled alike, four filled buttons read as four
                of the same thing and nothing said which pair excluded. */}
            <div className="ml-auto flex items-center gap-2">
              <Button
                variant={showScenarios ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setShowScenarios(s => !s)}
                disabled={!farmId}
                aria-label="Scenarios"
                aria-pressed={showScenarios}
                title="Saved valve sets to solve against"
              >
                <FlaskConical className="w-4 h-4" /><span className="hidden sm:inline">Scenarios</span>
              </Button>
              <Button
                variant={showLive ? 'primary' : 'outline'}
                size="sm"
                onClick={() => { setShowLive(s => !s); if (showLive) setLiveAnalysis(null); }}
                disabled={!farmId}
                aria-label="Live"
                aria-pressed={showLive}
                title="Solve current relay state and compare to pressure sensors"
              >
                <Activity className="w-4 h-4" /><span className="hidden sm:inline">Live</span>
              </Button>

              <div role="group" aria-label="Mode" className="inline-flex rounded-lg border border-gray-300 p-0.5 gap-0.5">
                <Button
                  variant={mode === 'view' ? 'primary' : 'ghost'}
                  size="sm"
                  className="h-8 shadow-none"
                  onClick={() => { setMode('view'); setTool('select'); setPipeStartNodeId(null); setPipeVertices([]); }}
                  aria-label="View"
                  aria-pressed={mode === 'view'}
                  title="Inspect the network without editing it"
                >
                  <Eye className="w-4 h-4" /><span className="hidden sm:inline">View</span>
                </Button>
                <Button
                  variant={mode === 'edit' ? 'primary' : 'ghost'}
                  size="sm"
                  className="h-8 shadow-none"
                  onClick={() => setMode('edit')}
                  aria-label="Edit network"
                  aria-pressed={mode === 'edit'}
                  title="Add nodes and draw pipes"
                >
                  <Pencil className="w-4 h-4" /><span className="hidden sm:inline">Edit network</span>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Edit toolbar */}
        {mode === 'edit' && farmId && (
          <Card className="mb-3">
            <CardContent className="p-3 sm:px-4 flex flex-wrap items-center gap-2">
              <Button variant={tool === 'select' ? 'primary' : 'outline'} size="sm"
                onClick={() => { setTool('select'); setPipeStartNodeId(null); setPipeVertices([]); }}>
                Select
              </Button>
              <div className="flex items-center gap-1">
                <Button variant={tool === 'add-node' ? 'primary' : 'outline'} size="sm"
                  onClick={() => setTool('add-node')}>
                  <Plus className="w-4 h-4" />Add node
                </Button>
                {tool === 'add-node' && (
                  <select value={pendingNodeType}
                    onChange={(e) => setPendingNodeType(e.target.value as NodeType)}
                    className="border rounded px-2 py-1 text-sm">
                    <option value="source">Source</option>
                    <option value="valve">Valve</option>
                    <option value="junction">Junction</option>
                    <option value="sensor">Sensor</option>
                    <option value="outlet">Outlet</option>
                    <option value="tank">Tank</option>
                  </select>
                )}
              </div>
              <Button variant={tool === 'draw-pipe' ? 'primary' : 'outline'} size="sm"
                onClick={() => { setTool('draw-pipe'); setPipeStartNodeId(null); setPipeVertices([]); }}>
                Draw pipe
              </Button>
              {tool === 'draw-pipe' && (
                <span className="text-xs text-gray-600">
                  {pipeStartNodeId === null
                    ? 'Click first node…'
                    : 'Click intermediate points (optional), then second node to finish.'}
                </span>
              )}
            </CardContent>
          </Card>
        )}

        {error && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {error}
          </div>
        )}

        {!farmId ? (
          <Card><CardContent className="p-4">Choose a farm above to view its irrigation network.</CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="lg:col-span-2" style={{ minHeight: 500 }}>
              <MapContainer
                center={center}
                zoom={17}
                style={{ height: '70vh', width: '100%', borderRadius: 8 }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  maxZoom={22}
                />
                <MapClickHandler onClick={handleMapClick} />
                <FitToFarm points={fitPoints} fitKey={fitKey} />
                {/* Edit mode is exempt: someone placing nodes is working on
                    the map itself, and the toolbar that put them there is
                    directly above it. */}
                <MapTouchGate disabled={mode === 'edit' || zoneDraft !== null} />

                {/* Drawing a relay's area. The same control the controller's
                    live map uses, so a zone drawn from either page is the same
                    shape of thing. */}
                {zoneDraft && (
                  <ZoneDrawControl
                    color={zoneDraft.zone?.zone_color ?? '#3B82F6'}
                    existingGeometry={zoneDraft.zone?.boundary_geometry ?? null}
                    onZoneDrawn={async (geometry) => {
                      try {
                        const saved = await espnowService.updateRelayZone(
                          zoneDraft.router_mac, zoneDraft.relay_index, zoneDraft.controller_id,
                          {
                            // A zone drawn before it is named takes the relay's
                            // own label, so it is findable rather than blank.
                            zone_name: zoneDraft.zone?.zone_name ?? zoneDraft.label,
                            zone_color: zoneDraft.zone?.zone_color ?? '#3B82F6',
                            boundary_geometry: geometry,
                            notes: zoneDraft.zone?.notes ?? null,
                          },
                        );
                        applyZone(saved, saved.router_mac, saved.relay_index);
                      } catch (e) {
                        setError(errorMessage(e, 'Could not save the area'));
                      } finally {
                        setZoneDraft(null);
                      }
                    }}
                    onCancel={() => setZoneDraft(null)}
                  />
                )}

                {/* Farm boundary — context for placing nodes, deliberately quiet.
                    Non-interactive so clicks fall through to the map and still
                    place a node when the add-node tool is armed. */}
                {farmRing && (
                  <Polygon
                    positions={farmRing}
                    interactive={false}
                    pathOptions={{
                      color: '#F9FAFB',
                      weight: 2,
                      dashArray: '6 6',
                      fillColor: '#F9FAFB',
                      fillOpacity: 0.04,
                    }}
                  />
                )}

                {/* The areas the valves water. Drawn under the pipes so a
                    main still reads across one, and non-interactive so a click
                    reaches the node or the map beneath rather than being eaten
                    by a polygon covering half the farm. The node's own marker
                    is how a zone is selected. */}
                {relayCatalogue.map((slot) => {
                  const ring = slot.zone?.boundary_geometry?.coordinates?.[0];
                  if (!ring || ring.length < 3) return null;
                  // Dimmed unless this relay's valve is the selected node, so
                  // one area stands out of a farm covered in them.
                  const mine = selectedNode !== null
                    && slot.linked_node_id === selectedNode.id;
                  return (
                    <Polygon
                      key={`zone-${slotKey(slot)}`}
                      positions={geoJsonRingToLatLng(ring)}
                      interactive={false}
                      pathOptions={{
                        color: slot.zone!.zone_color,
                        weight: mine ? 3 : 1,
                        fillColor: slot.zone!.zone_color,
                        fillOpacity: mine ? 0.3 : 0.12,
                        opacity: mine ? 0.95 : 0.5,
                      }}
                    />
                  );
                })}

                {network?.pipes.map((pipe) => {
                  const latlngs = pipe.geometry.coordinates.map(([lng, lat]) => [lat, lng]) as [number, number][];
                  const pr = effectiveSolve?.pipes.find(p => p.id === pipe.id);
                  const colour = pr ? velocityColour(pr.velocity_ms) : '#0ea5e9';
                  const weight = pr ? Math.min(8, 3 + Math.abs(pr.flow_lpm) / 100) : 4;
                  return (
                    <Polyline
                      key={pipe.id}
                      positions={latlngs}
                      pathOptions={{ color: colour, weight, opacity: 0.9 }}
                      eventHandlers={{ click: () => { setSelectedPipe(pipe); setSelectedNode(null); } }}
                    >
                      <Tooltip>
                        <div>
                          <strong>{pipe.nominal_diameter_mm}mm {materials.find(m => m.key === pipe.material_key)?.display_name ?? pipe.material_key}</strong>
                          <div>Length: {pipe.length_m}m</div>
                          {pr && (
                            <>
                              <div>Flow: {pr.flow_lpm} L/min</div>
                              <div>Velocity: {pr.velocity_ms} m/s</div>
                              <div>Headloss: {pr.headloss_m}m</div>
                            </>
                          )}
                        </div>
                      </Tooltip>
                    </Polyline>
                  );
                })}

                {/* Live pipe-being-drawn preview */}
                {tool === 'draw-pipe' && pipeVertices.length > 0 && (
                  <Polyline
                    positions={pipeVertices.map(([lng, lat]) => [lat, lng]) as [number, number][]}
                    pathOptions={{ color: '#f59e0b', weight: 3, dashArray: '6 6' }}
                  />
                )}

                {network?.nodes.map((n) => {
                  const isRelay = n.relay_router_mac && n.relay_index !== null;
                  const liveKey = isRelay ? `${n.relay_router_mac}:${n.relay_index}` : null;
                  const live = liveKey ? network.relay_state[liveKey] : null;
                  const sensor = n.sensor_channel_id ? network.sensor_readings[n.sensor_channel_id] : null;
                  const div = divergenceByNode.get(n.id);
                  const ringColour = div
                    ? divergenceColour(div.level)
                    : (live?.on ? '#22c55e' : (live ? '#9ca3af' : NODE_COLOURS[n.node_type]));
                  return (
                    <CircleMarker
                      key={n.id}
                      center={[n.lat, n.lng]}
                      radius={NODE_RADIUS[n.node_type]}
                      pathOptions={{
                        color: ringColour,
                        fillColor: NODE_COLOURS[n.node_type],
                        fillOpacity: 0.9,
                        weight: 3,
                      }}
                      eventHandlers={{ click: () => handleNodeClick(n) }}
                    >
                      <Tooltip>
                        <div>
                          <strong>{n.name || `${n.node_type} #${n.id}`}</strong>
                          {n.elevation_m !== null && <div>Elev: {n.elevation_m}m</div>}
                          {(() => {
                            const nr = effectiveSolve?.nodes.find(x => x.id === n.id);
                            if (!nr) return null;
                            return (
                              <>
                                {nr.pressure_kpa != null && <div>Predicted: {nr.pressure_kpa} kPa</div>}
                                {nr.demand_lpm > 0 && <div>Demand: {nr.demand_lpm} L/min</div>}
                              </>
                            );
                          })()}
                          {div && (
                            <div className={
                              div.level === 'alert' ? 'text-red-700' :
                              div.level === 'warn' ? 'text-amber-700' :
                              div.level === 'stale' ? 'text-gray-600' : 'text-green-700'
                            }>
                              Measured: {div.measured_kpa.toFixed(0)} kPa
                              {div.delta_pct != null && ` (${div.delta_pct > 0 ? '+' : ''}${div.delta_pct.toFixed(0)}%)`}
                            </div>
                          )}
                          {sensor && !div && <div>Sensor: {sensor.value} ({sensor.type_key})</div>}
                          {isRelay && <div>Relay: {live?.on ? 'ON' : 'OFF'}</div>}
                        </div>
                      </Tooltip>
                      <Popup>
                        <div className="text-xs">
                          <div className="font-semibold">{n.name || `${n.node_type} #${n.id}`}</div>
                          <div>{n.lat.toFixed(6)}, {n.lng.toFixed(6)}</div>
                          {mode === 'edit' && (
                            <Button size="sm" variant="outline" className="mt-2"
                              onClick={() => setSelectedNode(n)}>
                              Edit
                            </Button>
                          )}
                        </div>
                      </Popup>
                    </CircleMarker>
                  );
                })}
              </MapContainer>

              {loading && <div className="text-xs text-gray-500 mt-1">Loading network…</div>}
            </div>

            {/* Sidebar: live analysis + scenarios + selection details */}
            <div className="space-y-2">
              {showLive && farmId && (
                <LiveAnalysisPanel
                  farmId={farmId}
                  data={liveAnalysis}
                  setData={setLiveAnalysis}
                  onJump={({ node_id, pipe_id }) => {
                    if (!network) return;
                    if (node_id) {
                      const n = network.nodes.find(x => x.id === node_id);
                      if (n) { setSelectedNode(n); setSelectedPipe(null); }
                    } else if (pipe_id) {
                      const p = network.pipes.find(x => x.id === pipe_id);
                      if (p) { setSelectedPipe(p); setSelectedNode(null); }
                    }
                  }}
                />
              )}
              {showScenarios && network && farmId && (
                <ScenariosPanel
                  farmId={farmId}
                  nodes={network.nodes}
                  activeScenarioId={activeScenarioId}
                  setActiveScenarioId={setActiveScenarioId}
                  draftOpenValves={draftOpenValves}
                  setDraftOpenValves={setDraftOpenValves}
                  result={solveResult}
                  setResult={setSolveResult}
                  onJump={({ node_id, pipe_id }) => {
                    if (node_id) {
                      const n = network.nodes.find(x => x.id === node_id);
                      if (n) { setSelectedNode(n); setSelectedPipe(null); }
                    } else if (pipe_id) {
                      const p = network.pipes.find(x => x.id === pipe_id);
                      if (p) { setSelectedPipe(p); setSelectedNode(null); }
                    }
                  }}
                />
              )}
              {selectedNode && (
                <NodeEditDrawer
                  node={selectedNode}
                  network={network}
                  editable={mode === 'edit'}
                  catalogue={relayCatalogue}
                  routersByController={routersByController}
                  catalogueLoading={catalogueLoading}
                  catalogueError={catalogueError}
                  drawingZoneFor={zoneDraft ? slotKey(zoneDraft) : null}
                  onDrawZone={(slot) => setZoneDraft(slot)}
                  onCancelDrawZone={() => setZoneDraft(null)}
                  onZoneSaved={applyZone}
                  onClose={() => setSelectedNode(null)}
                  onSaved={async () => { await loadNetwork(true); }}
                  onDeleted={async () => { setSelectedNode(null); await loadNetwork(true); }}
                />
              )}
              {selectedPipe && (
                <PipeEditDrawer
                  pipe={selectedPipe}
                  materials={materials}
                  editable={mode === 'edit'}
                  onClose={() => setSelectedPipe(null)}
                  onSaved={async () => { await loadNetwork(true); }}
                  onDeleted={async () => { setSelectedPipe(null); await loadNetwork(true); }}
                />
              )}
              {!selectedNode && !selectedPipe && (
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-gray-600">
                      {mode === 'view'
                        ? 'Click a node or pipe to inspect.'
                        : 'Use the toolbar to add nodes or draw pipes. Click any node or pipe to edit.'}
                    </div>
                    {network && (
                      <div className="mt-3 text-xs text-gray-500">
                        {network.nodes.length} nodes · {network.pipes.length} pipes
                      </div>
                    )}
                    {unlinkedRelays.length > 0 && (
                      // The hydraulic check reports these as "unmapped relays"
                      // from inside a program. Said here too, because this is
                      // the page where the link is made and the check is not.
                      <div className="mt-2 text-xs text-amber-700">
                        {unlinkedRelays.length} scheduled relay
                        {unlinkedRelays.length !== 1 ? 's have' : ' has'} no valve node —
                        a step that opens {unlinkedRelays.length !== 1 ? 'them' : 'it'} solves
                        as though that valve were shut. Link {unlinkedRelays.length !== 1 ? 'them' : 'it'} from
                        a valve&rsquo;s Device link panel.
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {selectedNode && mode === 'edit' && (
                <Button
                  variant="outline" size="sm" className="mt-2 text-red-600"
                  onClick={async () => {
                    if (!confirm('Delete this node?')) return;
                    try {
                      await irrigationNetworkService.deleteNode(selectedNode.id);
                      setSelectedNode(null);
                      await loadNetwork(true);
                    } catch (e) { setError(errorMessage(e, 'Delete failed')); }
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Delete node
                </Button>
              )}
              {selectedPipe && mode === 'edit' && (
                <Button
                  variant="outline" size="sm" className="mt-2 text-red-600"
                  onClick={async () => {
                    if (!confirm('Delete this pipe?')) return;
                    try {
                      await irrigationNetworkService.deletePipe(selectedPipe.id);
                      setSelectedPipe(null);
                      await loadNetwork(true);
                    } catch (e) { setError(errorMessage(e, 'Delete failed')); }
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Delete pipe
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
